

import type { FormEvent, FormHTMLAttributes, ReactNode } from 'react';
import {
  CircleNotchIcon,
} from '@phosphor-icons/react'
import { 
  Dialog, 
  DialogContent, 
  DialogHeader,   
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface DialogPatternProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  icon?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  isLoading?: boolean;
  onCancel?: () => void;
  onSubmit?: ((e?: FormEvent) => void) | (() => void);
  submitLabel?: string;
  cancelLabel?: string;
  maxWidth?: string;
  className?: string;
  hideCloseButton?: boolean;
  contentClassName?: string;
  form?: string;
  isSubmitting?: boolean;
  isForm?: boolean;
  formProps?: FormHTMLAttributes<HTMLFormElement>;
  wrapContentInCard?: boolean;
  contentCardClassName?: string;
  iconSize?: 'sm' | 'md' | 'lg';
  titleSize?: 'sm' | 'md' | 'lg';
  modal?: boolean;
}

/**
 * A standardized dialog pattern component that ensures consistent styling and layout
 * for all dialogs in the application
 */
export function DialogPattern({
  open,
  onOpenChange,
  title,
  description,
  icon,
  children,
  footer,
  isLoading = false,
  onCancel,
  onSubmit,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  maxWidth = 'sm:max-w-[500px]',
  className,
  hideCloseButton = false,
  contentClassName,
  form,
  isSubmitting = false,
  isForm = false,
  formProps,
  wrapContentInCard = false,
  contentCardClassName,
  iconSize = 'md',
  titleSize = 'md',
  modal = true
}: DialogPatternProps) {
  const shouldRenderFooter = footer !== null;
  const hasCustomFooter = footer !== undefined && footer !== null;

  // Default cancel handler if none provided
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onOpenChange(false);
    }
  };

  // Icon size classes
  const iconSizeClasses = {
    sm: 'h-10 w-10',
    md: 'h-12 w-12', 
    lg: 'h-16 w-16'
  };

  // Title size classes
  const titleSizeClasses = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl'
  };

  // Content wrapper
  const ContentWrapper = isForm ? 'form' : 'div';
  const contentWrapperProps = isForm ? { 
    onSubmit: onSubmit,
    ...formProps 
  } : {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={modal}>
      <DialogContent 
        className={cn(
          maxWidth, 
          "border bg-background shadow-lg rounded-lg p-0 overflow-hidden flex flex-col",
          className
        )}
        hideCloseButton={hideCloseButton}
      >
        <ContentWrapper 
          {...contentWrapperProps}
          className="flex flex-col min-h-0 h-full"
        >
          {/* Header section - fixed */}
          <div className="flex-shrink-0">
            {icon && (
              <div className="flex justify-center mb-3 pt-4 sm:pt-5">
                <div className={cn(
                  "rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center",
                  iconSizeClasses[iconSize]
                )}>
                  {icon}
                </div>
              </div>
            )}
            
            <DialogHeader className="px-4 sm:px-5 pb-2 sm:pb-3">
              <DialogTitle className={cn(
                "font-medium tracking-tight text-center",
                titleSizeClasses[titleSize]
              )}>
                {title}
              </DialogTitle>
              {description && (
                <DialogDescription className="text-center max-w-md mx-auto text-xs sm:text-sm mt-1">
                  {description}
                </DialogDescription>
              )}
            </DialogHeader>
          </div>
          
          {/* Content section - scrollable */}
          <div className={cn(
            "flex-1 overflow-y-auto px-4 sm:px-5 py-2 sm:py-3",
            contentClassName
          )}>
            {wrapContentInCard ? (
              <div className={cn(
                "bg-card rounded-lg p-4 sm:p-5 border",
                contentCardClassName
              )}>
                {children}
              </div>
            ) : (
              children
            )}
          </div>
          
          {/* Footer section - fixed */}
          {shouldRenderFooter && (
            <DialogFooter className="flex-shrink-0 px-4 sm:px-5 pb-4 sm:pb-5 pt-3 sm:pt-4 flex justify-center border-t">
              {hasCustomFooter ? (
                footer
              ) : (
                <>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="rounded-full h-8 sm:h-9 px-3 sm:px-4 text-sm"
                  >
                    {cancelLabel}
                  </Button>
                  {onSubmit && (
                    <Button 
                      type={form ? "submit" : "button"}
                      onClick={form ? undefined : onSubmit}
                      disabled={isLoading || isSubmitting}
                      className="rounded-full h-8 sm:h-9 px-4 sm:px-5 ml-2 text-sm"
                      form={form}
                    >
                      {isLoading ? (
                        <>
                          <CircleNotchIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : isSubmitting ? (
                        <>
                          <CircleNotchIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : submitLabel}
                    </Button>
                  )}
                </>
              )}
            </DialogFooter>
          )}
        </ContentWrapper>
      </DialogContent>
    </Dialog>
  );
} 
